// Fiche d'un programme de livraison (ADL) : à livrer / livré / reste par type, et tableau des camions.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, GlobalStyles, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { EditOutlined, PrintOutlined } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { marcheAPI } from '../../services/api';
import { apiError, dateFr } from '../../utils/format';
import { tokens } from '../../theme';
import { AdlDialog, Avancement, typeLabel } from './common';

const Adl = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [a, setA] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.info || '');
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => marcheAPI.adl(id).then((r) => setA(r.data)).catch((e) => setError(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!a) return <Layout title="Programme de livraison">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const camions = new Set(a.livraisons.map((l) => l.bl).filter(Boolean)).size;

  return (
    <Layout title={`${a.libelle}${a.periode ? ` — ${a.periode}` : ''}`} plain
      actions={(
        <>
          <Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>
          {a.ecriture && <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => setEdit(true)}>Modifier</Button>}
        </>
      )}>
      <GlobalStyles styles={{ '@media print': { 'nav, aside, .MuiDrawer-root, .no-print, button': { display: 'none !important' }, body: { background: '#fff !important' } } }} />
      {info && <Alert className="no-print" severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Chip label={a.statut === 'CLOS' ? 'Clos' : 'En cours'} color={a.statut === 'CLOS' ? 'default' : 'info'} />
        <Typography color="text.secondary">{a.client_nom}{a.date_adl ? ` · du ${dateFr(a.date_adl)}` : ''} · {camions} BL</Typography>
        <Box sx={{ flex: 1 }} /><Box sx={{ width: 220 }}><Avancement value={a.totaux.avancement} /></Box>
      </Box>
      {a.sans_type > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{a.sans_type} poteau(x) livré(s) sans type précisé dans le fichier repris : à vérifier.</Alert>}
      {a.notes && <Alert severity="info" icon={false} sx={{ mb: 2, whiteSpace: 'pre-line' }}>{a.notes}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <Panel title="Suivi de l'ADL" flush>
            <Table size="small">
              <TableHead><TableRow><TableCell>Type</TableCell><TableCell align="right">À livrer</TableCell><TableCell align="right">Livré</TableCell><TableCell align="right">Reste</TableCell></TableRow></TableHead>
              <TableBody>
                {a.lignes.map((l) => (
                  <TableRow key={l.pba_type}>
                    <TableCell sx={{ fontWeight: 600 }}>{typeLabel(l.pba_type)}{l.hors_programme && <Chip size="small" label="hors ADL" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} color="warning" variant="outlined" />}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{l.a_livrer}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{l.livre}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: l.reste ? 700 : 400, color: l.reste > 0 ? '#B4232A' : l.reste < 0 ? '#8A5A0B' : tokens.steel }}>
                      {l.reste < 0 ? `+${-l.reste}` : l.reste}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${tokens.line}`, fontFamily: tokens.mono } }}>
                  <TableCell sx={{ fontFamily: 'inherit !important' }}>Total</TableCell><TableCell align="right">{a.totaux.a_livrer}</TableCell><TableCell align="right">{a.totaux.livre}</TableCell><TableCell align="right">{a.totaux.reste}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', p: 1.5 }}>Reste en rouge : à livrer. « +n » : livré au-delà du programme.</Typography>
          </Panel>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <Panel title={`Camions livrés (${a.livraisons.length} lignes)`} flush>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell><TableCell>Destination</TableCell><TableCell>BL</TableCell><TableCell>Type</TableCell><TableCell align="right">Qté</TableCell>
                    <TableCell>Transporteur</TableCell><TableCell>Véhicule</TableCell><TableCell>Chauffeur</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {a.livraisons.map((l, i) => (
                    <TableRow key={`${l.source}-${l.id || l.delivery_id}-${l.pba_type}-${i}`} hover={l.source === 'ERP'} sx={{ cursor: l.source === 'ERP' ? 'pointer' : 'default' }}
                      onClick={() => l.source === 'ERP' && navigate(`/bons-livraison/${l.delivery_id}`)}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(l.date)}</TableCell>
                      <TableCell>{l.destination || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>
                        {l.bl || '—'}{l.hors_adl && <Chip size="small" label="sans ADL" sx={{ ml: 0.5, height: 18, fontSize: 10.5 }} variant="outlined" />}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: l.pba_type ? undefined : '#B4232A' }}>{l.pba_type ? typeLabel(l.pba_type) : 'non précisé'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{l.quantite}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{l.transporteur || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{l.vehicule || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{l.chauffeur || '—'}{l.telephone ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{l.telephone}</Typography> : null}</TableCell>
                    </TableRow>
                  ))}
                  {!a.livraisons.length && <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucune livraison. Choisissez cet ADL lors de la création du BL.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Panel>
        </Grid>
      </Grid>
      <AdlDialog open={edit} adl={a} onClose={() => setEdit(false)} onSaved={(r) => { setEdit(false); setInfo(r.message); load(); }} />
    </Layout>
  );
};

export default Adl;
